import { api } from "./api";

// --- types ------------------------------------------------------------------

/**
 * Which client may act inside the owner's account for this role:
 * "all" = web dashboard + driver app (default), "web" = web dashboard only,
 * "app" = driver app only. The user's own account is never restricted.
 */
export type ClientAccess = "all" | "web" | "app";

export interface AccountMembership {
  accountId: string;
  name: string | null;
  email: string;
  role: "owner" | "member";
  isSelf: boolean;
  subscriptionPaid: boolean;
  cameraInstalled: boolean;
  /** Present only on the isSelf entry: the user pays THEMSELVES (not inherited). */
  canManageTeam?: boolean;
  // present only for role === "member"
  groupName?: string;
  allFarms?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canExport?: boolean;
  canAccessDevices?: boolean;
  canViewFields?: boolean;
  canRecordLoads?: boolean;
  canCalibrate?: boolean;
  clientAccess?: ClientAccess;
}

export interface AccessGroup {
  id: string;
  name: string;
  allFarms: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  // "Access machines & all loads" in the UI.
  canAccessDevices: boolean;
  // Fields, boundaries, yield maps, field analytics. false hides them entirely.
  canViewFields: boolean;
  // Create weight records (loads) for machines in scope, without needing canEdit.
  canRecordLoads: boolean;
  // Upload calibration runs from the driver app for machines in scope (spec §7).
  canCalibrate: boolean;
  clientAccess: ClientAccess;
  farmIds: string[];
  memberCount: number;
}

export interface TeamMember {
  memberUserId: string;
  name: string | null;
  email: string;
  groupId: string;
  groupName: string;
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  groupId: string;
  groupName: string;
  status: string;
  createdAt: string;
  expiresAt: string;
}

export interface GroupInput {
  name: string;
  allFarms: boolean;
  farmIds: string[];
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canAccessDevices: boolean;
  canViewFields: boolean;
  canRecordLoads: boolean;
  canCalibrate: boolean;
  clientAccess: ClientAccess;
}

/**
 * Fill the driver-access fields when the backend predates them, with the same
 * defaults its migration backfills (fields visible, loads = edit + machines,
 * no calibration, both clients). Once the backend sends them this is a no-op.
 */
export function normalizeGroup(
  g: Omit<AccessGroup, "canViewFields" | "canRecordLoads" | "canCalibrate" | "clientAccess"> &
    Partial<Pick<AccessGroup, "canViewFields" | "canRecordLoads" | "canCalibrate" | "clientAccess">>
): AccessGroup {
  return {
    ...g,
    canViewFields: g.canViewFields ?? true,
    canRecordLoads: g.canRecordLoads ?? (g.canEdit && g.canAccessDevices),
    canCalibrate: g.canCalibrate ?? false,
    clientAccess: g.clientAccess ?? "all",
  };
}

// --- account switcher -------------------------------------------------------

/**
 * Redeem an emailed invitation token for the signed-in user. Silent: the caller
 * treats a refusal (used, expired, another recipient) as "nothing to join".
 */
export const acceptInvitation = (token: string) =>
  api.post<unknown>("/api/v1/team/invitations/accept", { token }, { silent: true });

export const getAccounts = async (): Promise<AccountMembership[]> => {
  const body = await api.get<{ accounts: AccountMembership[] }>(
    "/api/v1/team/accounts"
  );
  return body.accounts ?? [];
};

// --- groups -----------------------------------------------------------------

export const listGroups = async (): Promise<AccessGroup[]> => {
  const body = await api.get<{ groups: Parameters<typeof normalizeGroup>[0][] }>(
    "/api/v1/team/groups"
  );
  return (body.groups ?? []).map(normalizeGroup);
};

export const createGroup = async (input: GroupInput): Promise<string> => {
  const body = await api.post<{ id: string }>("/api/v1/team/groups", input);
  return body.id;
};

export const updateGroup = async (id: string, input: GroupInput): Promise<void> => {
  await api.patch(`/api/v1/team/groups/${id}`, input);
};

export const deleteGroup = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/team/groups/${id}`);
};

// --- members ----------------------------------------------------------------

export const listMembers = async (): Promise<TeamMember[]> => {
  const body = await api.get<{ members: TeamMember[] }>("/api/v1/team/members");
  return body.members ?? [];
};

export const updateMemberGroup = async (
  memberUserId: string,
  groupId: string
): Promise<void> => {
  // Silent: TeamScreen toasts the failure itself (two toasts otherwise).
  await api.patch(`/api/v1/team/members/${memberUserId}`, { groupId }, { silent: true });
};

export const removeMember = async (memberUserId: string): Promise<void> => {
  await api.delete(`/api/v1/team/members/${memberUserId}`, undefined, { silent: true });
};

// --- invitations ------------------------------------------------------------

export interface CheckEmailResult {
  exists: boolean;
  alreadyMember: boolean;
  isSelf: boolean;
}

export const checkEmail = async (email: string): Promise<CheckEmailResult> => {
  // Silent: InviteDialog toasts the failure itself.
  return api.get<CheckEmailResult>(
    `/api/v1/team/check-email?email=${encodeURIComponent(email)}`,
    undefined,
    { silent: true }
  );
};

export const invite = async (
  email: string,
  groupId: string
): Promise<{ path: "added" | "invited" }> => {
  return api.post<{ path: "added" | "invited" }>(
    "/api/v1/team/invitations",
    { email, groupId },
    { silent: true }
  );
};

export const listInvitations = async (): Promise<Invitation[]> => {
  const body = await api.get<{ invitations: Invitation[] }>(
    "/api/v1/team/invitations"
  );
  return body.invitations ?? [];
};

export const revokeInvitation = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/team/invitations/${id}`, undefined, { silent: true });
};

export const resendInvitation = async (id: string): Promise<void> => {
  await api.post(`/api/v1/team/invitations/${id}/resend`, {}, { silent: true });
};
